/**
 * useFocusScroll — scroll an entity-tagged DOM node into view and add a
 * transient `.is-focused` class (doc 147 §5.3 PR C — URL prefilter params).
 *
 * Used by analysis views (Patterns / Compliance / Refactor / Heatmap /
 * Drift / Simulate) to honour the `?focus=<entityId>` prefilter that the
 * entity command palette emits. Views just need to render
 * `data-entity-id="<id>"` on their per-entity rows; this hook does the
 * scroll + the visual cue.
 */
import { useEffect } from 'react';

const FOCUS_CLASS = 'is-focused';

export function useFocusScroll(entityId: string | undefined): void {
  useEffect(() => {
    if (!entityId || typeof document === 'undefined') return;
    const target = document.querySelector(
      `[data-entity-id="${cssEscape(entityId)}"]`,
    ) as HTMLElement | null;
    if (!target) return;
    target.classList.add(FOCUS_CLASS);
    // `behavior: 'instant'` if reduced-motion; otherwise smooth.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: prefersReduced ? 'instant' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });
    return () => {
      target.classList.remove(FOCUS_CLASS);
    };
  }, [entityId]);
}

/** Escape an entity id for use inside a CSS attribute selector. */
function cssEscape(id: string): string {
  return id.replace(/(["\\])/g, '\\$1');
}
